import { CheckboxCustomEvent, IonAlert, IonCheckbox, IonItem, IonLabel, IonList, IonListHeader, IonLoading, IonModal } from "@ionic/react";
import { ReportCategory, UserMessage } from "@sendbird/chat/lib/__definition";
import { useSendbirdStateContext } from "@wearesweet/sendbird-uikit-react";
import { useGroupChannelContext } from "@wearesweet/sendbird-uikit-react/GroupChannel/context";
import { t } from "i18next";
import React, { useState } from "react";
import { debug } from "../../../App";

export interface MessageReportRequest {
  channel_type: string;
  channel_url: string;
  message: string;
}

export const ReportModal = (props: any) => {
  const [reasons] = useState<string[]>(["suspicious", "harassing", "inappropriate", "spam"]);
  const [reason, setReason] = useState<ReportCategory | null>(null);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const channel = useGroupChannelContext();
  const sdk = useSendbirdStateContext();
  const [block, setBlock] = useState<boolean>(false);

  const sendReport = async () => {
    if (reason) {
      debug("Sending report", props.report);

      const message = (await sdk.stores.sdkStore.sdk.message.getMessage({
        channelUrl: props.report.channel_url,
        channelType: props.report.channel_type,
        messageId: props.report.message,
      })) as UserMessage;

      if (block) {
        debug("Blocking user", props.report.member_id);
        await sdk.stores.sdkStore.sdk.blockUserWithUserId(props.report.member_id);
      }

      if (message) {
        debug("Message to report", message);
        await channel.currentChannel?.reportMessage(message, reason, "Reported via app");
      }

      return true;
    }
  };

  return (
    <>
      <IonAlert
        isOpen={reason !== null}
        header={t("areYouSure") || ""}
        message={t("reportConfirm") || ""}
        buttons={[
          {
            text: t("cancel"),
            role: "cancel",
            cssClass: "text-dark",
            handler: () => {
              setReason(null);
            },
          },
          {
            text: block ? t("reportAndBlock") : t("report"),
            cssClass: "text-report",
            handler: () => {
              setIsLoading(true);

              sendReport()
                .then(() => {
                  setConfirmationVisible(true);
                })
                .catch((e) => {
                  debug(e);
                })
                .finally(() => {
                  props.onDidDismiss?.();
                  setReason(null);
                  setIsLoading(false);
                });
            },
          },
        ]}
      />
      <IonLoading isOpen={isLoading} />
      <IonAlert
        isOpen={confirmationVisible}
        onDidDismiss={() => {
          setConfirmationVisible(false);
        }}
        header={t("reportSent") || ""}
        message={(block ? t("reportSentAndBlockedMessage") : t("reportSentMessage")) || ""}
        buttons={[
          {
            text: t("Ok"),
            handler: () => {
              setConfirmationVisible(false);
              setReason(null);
              props.onDidDismiss?.();
            },
          },
        ]}
      />
      <IonModal
        isOpen={props.report !== null}
        initialBreakpoint={1}
        breakpoints={[0, 1]}
        swipeToClose={true}
        style={{
          "--height": "auto",
        }}
        onDidDismiss={() => {
          props.onDidDismiss?.();
        }}
      >
        <IonList>
          <IonListHeader>
            <IonLabel>{t("reportMessage")}</IonLabel>
          </IonListHeader>
          <IonItem>
            <IonLabel
              class="ion-text-wrap"
              style={{
                fontSize: "0.8rem",
                marginTop: 0,
                marginBottom: "1rem",
              }}
            >
              {t("reportIntro")}
            </IonLabel>
          </IonItem>

          <IonItem lines={"full"}>
            <IonCheckbox
              slot={"start"}
              color={"danger"}
              onIonChange={(e: CheckboxCustomEvent) => {
                setBlock(e.detail.checked);
              }}
            ></IonCheckbox>
            <IonLabel color={"danger"}>{t("blockMember")}</IonLabel>
          </IonItem>

          <IonItem>
            <IonLabel
              class="ion-text-wrap"
              style={{
                fontSize: "0.8rem",
                marginBottom: "1rem",
                marginTop: "1rem",
              }}
            >
              {t("whyReporting")}
            </IonLabel>
          </IonItem>

          {reasons.map((reason) => (
            <IonItem
              button
              key={reason}
              onClick={() => {
                setReason(reason as ReportCategory);
              }}
            >
              {t(reason)}
            </IonItem>
          ))}
        </IonList>
      </IonModal>
    </>
  );
};
